import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { canonicalPublicPath } from './addonNavigation';
import { getStoredAuthToken } from './api';

// The public application also serves native-app users. Never export an admin,
// development, Capacitor or callback URL into a customer-facing QR code.
export function customerPageUrl(module, slug) {
  const path = module?.enabled === true ? canonicalPublicPath(module.id, slug) : null;
  return path ? `https://beborne.com${path}` : null;
}
function sessionIdentity() {
  try {
    const account = JSON.parse(localStorage.getItem('kioskAccount') || 'null');
    return JSON.stringify([getStoredAuthToken(), account?.id || '', account?.tenantAccountId || '']);
  } catch { return ''; }
}

export default function CustomerPageSharing({ module, publicSlug }) {
  const url = customerPageUrl(module, publicSlug);
  return url ? <SharingControls key={url} module={module} publicSlug={publicSlug} url={url} /> : null;
}
function SharingControls({ module, publicSlug, url }) {
  const [owner] = useState(sessionIdentity);
  const [open, setOpen] = useState(false), [locked, setLocked] = useState(false);
  const [qr, setQr] = useState(''), [busy, setBusy] = useState(false), [message, setMessage] = useState('');
  const sequence = useRef(0), input = useRef(null), latest = useRef({ url, locked });
  latest.current = { url, locked };
  const clear = useCallback(() => { sequence.current += 1; setOpen(false); setQr(''); setBusy(false); setMessage(''); }, []);
  function valid() {
    if (!getStoredAuthToken() || !owner || owner !== sessionIdentity()) { clear(); setLocked(true); return false; }
    return !latest.current.locked && Boolean(latest.current.url);
  }
  useEffect(() => { clear(); }, [url, clear]); // A different slug or removed right discards the previous export.
  useEffect(() => {
    const check = () => { if (!getStoredAuthToken() || owner !== sessionIdentity()) { clear(); setLocked(true); } };
    const timer = window.setInterval(check, 250);
    window.addEventListener('storage', check); window.addEventListener('focus', check);
    return () => { sequence.current += 1; window.clearInterval(timer); window.removeEventListener('storage', check); window.removeEventListener('focus', check); };
  }, [owner, clear]);
  async function copy() {
    if (!valid()) return;
    const ticket = ++sequence.current, target = url;
    const current = () => ticket === sequence.current && latest.current.url === target && valid();
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(target);
      if (current()) setMessage('Lien copié.');
    } catch {
      if (!current()) return;
      input.current?.focus(); input.current?.select();
      setMessage('Copie automatique indisponible. Le lien est sélectionné : utilisez la commande Copier de votre appareil.');
    }
  }
  async function generate() {
    if (!valid() || busy) return;
    const ticket = ++sequence.current, target = url;
    setBusy(true); setQr(''); setMessage('');
    try {
      const image = await QRCode.toDataURL(target, { width: 320, margin: 4, errorCorrectionLevel: 'M', color: { dark: '#000000', light: '#ffffff' } });
      if (ticket !== sequence.current || latest.current.url !== target || !valid()) return;
      if (typeof image !== 'string' || !image.startsWith('data:image/png;base64,')) throw new Error('Invalid QR');
      setQr(image);
    } catch {
      if (ticket === sequence.current && latest.current.url === target && valid()) setMessage('Le QR code n’a pas pu être généré. Réessayez ou copiez le lien.');
    } finally { if (ticket === sequence.current) setBusy(false); }
  }
  function download() {
    if (!valid() || !qr) return;
    const link = document.createElement('a'); link.href = qr;
    link.download = `qr-${module.id}-${publicSlug}.png`; link.click();
  }
  if (!url) return null;
  const label = { beclick: 'BECLICK', beresa: 'Réservation', customer_chatbot: 'Assistant menu' }[module.id];
  return <div className="customer-page-sharing">
    {locked ? <p role="status">La session a changé. Rechargez Mes options avant de partager une page client.</p> : <>
      <button type="button" className="product-options-settings" aria-expanded={open} onClick={() => { if (open) clear(); else if (valid()) setOpen(true); }}>{open ? 'Fermer le partage' : `Partager la page ${label}`}</button>
      {open && <div role="group" aria-label={`Partager la page client ${label}`}>
        <p>Ce lien et ce QR ouvrent la page publique de cet établissement. Ils ne donnent aucun accès à votre administration.</p>
        <label>Lien public {label}<input ref={input} type="text" readOnly value={url} onFocus={event => event.target.select()} /></label>
        <div className="customer-page-sharing-actions"><button type="button" disabled={busy} onClick={copy}>Copier le lien</button><button type="button" disabled={busy} onClick={generate}>{busy ? 'Génération du QR…' : 'Afficher le QR code'}</button></div>
        {message && <p role="status">{message}</p>}
        {qr && <><img src={qr} width="320" height="320" alt={`QR code de la page client ${label}`} /><button type="button" onClick={download}>Télécharger le QR en PNG</button></>}
      </div>}
    </>}
  </div>;
}
